import { useState } from 'react'
import type { ChangeEvent, CSSProperties, FormEvent } from 'react'
import { C } from '../theme'
import { useContent } from '../content/ContentContext'
import { telHref } from '../content/derive'
import type { ContactPayload, ContactReason } from '../types'
import { submitContact } from '../lib/api'
import { WhatsAppIcon } from './icons'

const REASONS: { value: ContactReason; label: string }[] = [
  { value: 'oracion', label: 'Pedido de oración' },
  { value: 'ayuda', label: 'Quiero ayuda (para mí o un ser querido)' },
  { value: 'voluntario', label: 'Quiero ser voluntario' },
  { value: 'general', label: 'Información general' },
]

const labelStyle: CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 700, letterSpacing: '.08em',
  textTransform: 'uppercase', color: '#9fb4d6', marginBottom: 7,
}
const card: CSSProperties = {
  background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)',
  borderRadius: 12, padding: '30px 30px 32px',
}

export function ContactForm() {
  const contact = useContent().settings.contact ?? {}
  const whatsapp = useContent().settings.social?.whatsapp ?? undefined
  const tel = telHref(contact.phone)
  const [form, setForm] = useState<ContactPayload>({ name: '', phone: '', email: '', reason: 'oracion', message: '' })
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [error, setError] = useState('')

  const update =
    (key: keyof ContactPayload) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }))

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setStatus('submitting')
    setError('')
    try {
      await submitContact({ ...form, email: form.email?.trim() || undefined })
      setStatus('success')
    } catch (err) {
      setStatus('error')
      setError(err instanceof Error ? err.message : 'No se pudo enviar el mensaje.')
    }
  }

  if (status === 'success') {
    return (
      <div style={{ ...card, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#25D366', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>✓</div>
        <h3 style={{ fontFamily: "'Spectral',serif", fontWeight: 500, fontSize: 26, margin: 0, color: '#fff' }}>Gracias por escribirnos</h3>
        <p style={{ fontSize: 15.5, lineHeight: 1.6, color: '#cdd9ef', margin: 0 }}>
          Recibimos tu mensaje y te responderemos pronto. Si necesitas ayuda inmediata, llámanos o escríbenos por WhatsApp.
        </p>
        <a className="btn" href={whatsapp} target="_blank" rel="noopener" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: '#25D366', color: '#fff', fontWeight: 700, fontSize: 14.5, padding: '13px 24px', borderRadius: 40, textDecoration: 'none' }}>
          <WhatsAppIcon size={18} /> Escribir por WhatsApp
        </a>
      </div>
    )
  }

  const submitting = status === 'submitting'

  return (
    <form onSubmit={onSubmit} style={card} noValidate>
      <h3 style={{ fontFamily: "'Spectral',serif", fontWeight: 500, fontSize: 24, margin: '0 0 6px', color: '#fff' }}>Escríbenos</h3>
      <p style={{ fontSize: 14, color: '#9fb4d6', margin: '0 0 22px' }}>
        Cuéntanos cómo podemos acompañarte. Todo lo que compartas es confidencial.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label htmlFor="cf-name" style={labelStyle}>Nombre *</label>
          <input id="cf-name" className="field" value={form.name} onChange={update('name')} required placeholder="Tu nombre" autoComplete="name" />
        </div>
        <div>
          <label htmlFor="cf-phone" style={labelStyle}>Teléfono / WhatsApp *</label>
          <input id="cf-phone" className="field" value={form.phone} onChange={update('phone')} required placeholder="(939) 000-0000" inputMode="tel" autoComplete="tel" />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label htmlFor="cf-email" style={labelStyle}>Correo (opcional)</label>
        <input id="cf-email" className="field" type="email" value={form.email} onChange={update('email')} placeholder="tu@correo.com" autoComplete="email" />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label htmlFor="cf-reason" style={labelStyle}>Motivo</label>
        <select id="cf-reason" className="field" value={form.reason} onChange={update('reason')}>
          {REASONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label htmlFor="cf-message" style={labelStyle}>Mensaje *</label>
        <textarea id="cf-message" className="field" value={form.message} onChange={update('message')} required placeholder="¿Cómo podemos ayudarte?" rows={4} />
      </div>

      {status === 'error' && (
        <div role="alert" style={{ background: 'rgba(193,39,45,.18)', border: '1px solid rgba(193,39,45,.5)', color: '#ffd9d6', fontSize: 13.5, padding: '10px 14px', borderRadius: 6, marginBottom: 16 }}>
          {error} · Por favor llámanos al{' '}
          <a href={tel} style={{ color: '#fff', fontWeight: 700 }}>{contact.phoneDisplay}</a>.
        </div>
      )}

      <button className="btn" type="submit" disabled={submitting} style={{ width: '100%', background: C.red, color: '#fff', fontWeight: 700, fontSize: 15, padding: '15px 28px', borderRadius: 3, border: 0, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.75 : 1 }}>
        {submitting ? 'Enviando…' : 'Enviar mensaje'}
      </button>
    </form>
  )
}
